import React, { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useWeightEntries } from '../../../hooks/useWeightEntries'
import { useUnits } from '../../../contexts/UnitsContext'
import { cn } from '../../../lib/utils'
import { PhotoComparisonSlider } from './PhotoComparisonSlider'

const POSES = ['front', 'side', 'back', 'all']

function formatDateShort(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
}

function daysBetween(d1, d2) {
  const a = new Date(d1)
  const b = new Date(d2)
  const ms = Math.abs(a - b)
  return Math.round(ms / (1000 * 60 * 60 * 24))
}

export function PhotoComparisonModal({ photos, onClose }) {
  const { t } = useTranslation()
  const { entries: weightEntries } = useWeightEntries()
  const { weightUnit, formatWeight } = useUnits()
  const [selectedPose, setSelectedPose] = useState('front')
  const [firstId, setFirstId] = useState(null)
  const [secondId, setSecondId] = useState(null)

  // Filter photos by selected pose
  const filteredPhotos = useMemo(() => {
    const filtered = selectedPose === 'all' ? photos : photos.filter((p) => p.pose === selectedPose)
    // Sort chronologically (oldest first) for the picker strip
    return [...filtered].sort((a, b) => (a.date < b.date ? -1 : 1))
  }, [photos, selectedPose])

  // Auto-select oldest as first, newest as second when filter changes
  useEffect(() => {
    if (filteredPhotos.length >= 2) {
      setFirstId(filteredPhotos[0].id)
      setSecondId(filteredPhotos[filteredPhotos.length - 1].id)
    } else if (filteredPhotos.length === 1) {
      setFirstId(filteredPhotos[0].id)
      setSecondId(null)
    } else {
      setFirstId(null)
      setSecondId(null)
    }
  }, [filteredPhotos])

  // ESC + body scroll lock
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  const firstPhoto = filteredPhotos.find((p) => p.id === firstId)
  const secondPhoto = filteredPhotos.find((p) => p.id === secondId)

  // Look up matching weight entries by date
  const weightByDate = useMemo(() => {
    const map = new Map()
    for (const e of weightEntries) {
      map.set(e.date, e.weight)
    }
    return map
  }, [weightEntries])

  const firstWeight = firstPhoto ? weightByDate.get(firstPhoto.date) : null
  const secondWeight = secondPhoto ? weightByDate.get(secondPhoto.date) : null

  // Compute difference info
  let diffText = null
  if (firstPhoto && secondPhoto && firstPhoto.id !== secondPhoto.id) {
    const days = daysBetween(firstPhoto.date, secondPhoto.date)
    if (firstWeight != null && secondWeight != null) {
      const diff = Number(secondWeight) - Number(firstWeight)
      const sign = diff > 0 ? '+' : ''
      diffText = `${sign}${formatWeight(diff)} ${weightUnit} · ${days === 0 ? t('phoenixTracker.photos.sameDay') : t('phoenixTracker.photos.daysApart', { days })}`
    } else {
      diffText = days === 0 ? t('phoenixTracker.photos.sameDay') : t('phoenixTracker.photos.daysApart', { days })
    }
  }

  // PhotosTab sits inside PullToRefresh, whose CSS transform reparents
  // any `position: fixed` descendant to itself instead of the viewport.
  // Portal into document.body so `inset-0` actually measures the viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/95 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      style={{
        // Push the header below the iOS Dynamic Island / notch and keep
        // the bottom controls clear of the home indicator.
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        overscrollBehavior: 'contain',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0">
        <h2 className="text-white text-lg font-semibold">
          {t('phoenixTracker.photos.comparePhotos')}
        </h2>
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 p-4 max-w-2xl mx-auto w-full space-y-4">
        {/* Pose pills */}
        <div className="flex gap-2 flex-wrap justify-center">
          {POSES.map((pose) => (
            <button
              key={pose}
              onClick={() => setSelectedPose(pose)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                selectedPose === pose
                  ? 'bg-app-primary-500 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
              )}
            >
              {pose === 'all' ? t('phoenixTracker.photos.all') : t(`phoenixTracker.photos.${pose}`)}
            </button>
          ))}
        </div>

        {filteredPhotos.length < 2 ? (
          <div className="text-center text-white/60 py-12">
            {t('phoenixTracker.photos.needMorePhotos')}
          </div>
        ) : (
          <>
            {/* Date + weight summary */}
            <div className="flex items-center justify-center gap-3 text-white">
              <div className="text-center">
                <p className="text-xs text-white/60">{firstPhoto && formatDateShort(firstPhoto.date)}</p>
                {firstWeight != null && (
                  <p className="font-mono font-semibold text-sm">
                    {formatWeight(firstWeight)} {weightUnit}
                  </p>
                )}
              </div>
              <span className="text-white/40 text-sm">{t('phoenixTracker.photos.vs')}</span>
              <div className="text-center">
                <p className="text-xs text-white/60">{secondPhoto && formatDateShort(secondPhoto.date)}</p>
                {secondWeight != null && (
                  <p className="font-mono font-semibold text-sm">
                    {formatWeight(secondWeight)} {weightUnit}
                  </p>
                )}
              </div>
            </div>

            {/* Wipe slider */}
            {firstPhoto && secondPhoto && (
              <PhotoComparisonSlider
                beforeUrl={firstPhoto.signedUrl}
                afterUrl={secondPhoto.signedUrl}
                beforeLabel={formatDateShort(firstPhoto.date)}
                afterLabel={formatDateShort(secondPhoto.date)}
              />
            )}

            {/* Difference caption */}
            {diffText && (
              <p className="text-center text-app-primary-400 text-sm font-mono">
                {diffText}
              </p>
            )}

            {/* Photo picker strip — pick "before" */}
            <div>
              <p className="text-xs text-white/60 mb-2">{t('phoenixTracker.photos.selectFirst')}</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {filteredPhotos.map((p) => (
                  <button
                    key={`first-${p.id}`}
                    onClick={() => setFirstId(p.id)}
                    className={cn(
                      'shrink-0 w-16 aspect-square rounded-lg overflow-hidden border-2 transition-all',
                      firstId === p.id ? 'border-app-primary-500 scale-105' : 'border-white/20 opacity-60 hover:opacity-100'
                    )}
                  >
                    <img
                      src={p.signedUrl}
                      alt={p.date}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Photo picker strip — pick "after" */}
            <div>
              <p className="text-xs text-white/60 mb-2">{t('phoenixTracker.photos.selectSecond')}</p>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {filteredPhotos.map((p) => (
                  <button
                    key={`second-${p.id}`}
                    onClick={() => setSecondId(p.id)}
                    className={cn(
                      'shrink-0 w-16 aspect-square rounded-lg overflow-hidden border-2 transition-all',
                      secondId === p.id ? 'border-app-primary-500 scale-105' : 'border-white/20 opacity-60 hover:opacity-100'
                    )}
                  >
                    <img
                      src={p.signedUrl}
                      alt={p.date}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
